import { create } from "zustand"

import type { ArtifactsSlice } from "./slices/artifacts-state"
import type { ChatSlice, SessionState } from "./slices/chat-slice"
import type { ExplorerFeaturesSlice } from "./slices/explorer-features-slice"

import { createArtifactsSlice } from "./slices/artifacts-state"
import { createChatSlice, defaultSessionState } from "./slices/chat-slice"
import {
  type AskUserPayload,
  type DeepExplorerSessionState,
  type DeepTodo,
  defaultDeepExplorerSessionState,
} from "./slices/deep-explorer-session-state"
import { createExplorerFeaturesSlice } from "./slices/explorer-features-slice"
import { createNamespacedStore } from "./store-factory"

// ── Re-exports keep the public import path stable for the rest of the app.
export type {
  AskUserPayload,
  DeepActivityEvent,
  DeepExplorerSessionState,
  DeepFile,
  DeepSource,
  DeepTodo,
  DeepTodoStatus,
  OrchestratorReasoningBlock,
  QuestionItem,
} from "./slices/deep-explorer-session-state"
export {
  defaultDeepExplorerSessionState,
  getDeepStateForSession,
} from "./slices/deep-explorer-session-state"
export type {
  ReasoningMode,
  ReasoningEffortOverride,
  ResponseTiming,
} from "./slices/explorer-features-slice"
export type { ArtifactsSlice } from "./slices/artifacts-state"
export {
  getArtifactsForSession,
  getLatestPerLineage,
  getLatestPlanArtifact,
  getVersionChain,
  getArtifactById,
  getActiveArtifactId,
} from "./slices/artifacts-state"

// ── Store type ───────────────────────────────────────────────────────────────

type AIExplorerStore = ChatSlice &
  ExplorerFeaturesSlice &
  ArtifactsSlice & {
    // ── Deep Explorer namespace prefs ────────────────────────────────────────
    /** Is the panel expanded/open (in-memory, default true on first use).
     *  This is a user preference, not run state — survives session switches. */
    panelOpen: boolean
    /** When true, the Deep mode intro overlay stays hidden while idle (user
     *  clicked "Got it"). Persisted to localStorage so the dismissal sticks
     *  once and never re-shows to the same user on this browser. */
    deepModeIntroDismissedWhileIdle: boolean
    /** Sessions currently owned by an uncancellable synchronous REST Flow.
     *  Kept at the namespaced-store lifetime so page remounts do not forget
     *  an in-flight request. */
    uncancellableFlowRunSessionIds: ReadonlyMap<string, number>

    // ── Per-session deep state setters (sid required) ────────────────────────
    /** Merge a partial deep-state update into the slot for `sessionId`.
     *  Upserts the session row if it doesn't exist yet — useful when a WS
     *  frame for a background run arrives before `initSession` has run. */
    replaceDeepState: (sessionId: string, partial: Partial<DeepExplorerSessionState>) => void
    setDeepAutoApprove: (sessionId: string, auto: boolean) => void
    setDeepPlanProposed: (sessionId: string, proposed: boolean, todos?: DeepTodo[]) => void
    setDeepPlanApproved: (sessionId: string, approved: boolean) => void
    setAskUserProposed: (
      sessionId: string,
      proposed: boolean,
      payload?: AskUserPayload | null
    ) => void

    // ── Bridging setter (writes both layers) ─────────────────────────────────
    /** Toggles the global `panelOpen` pref. When `currentSessionId` is set,
     *  also writes `panelDismissedForSession` on that session's deep slot:
     *  closing the panel marks the current session as dismissed so the
     *  "default-open in a deep chat" effect can't immediately re-open it.
     *  Crosses both layers — keep this in mind when reading the diff. */
    setDeepPanelOpen: (open: boolean) => void

    // ── Namespace pref setters ───────────────────────────────────────────────
    setDeepModeIntroDismissedWhileIdle: (dismissed: boolean) => void

    // ── Namespace runtime-state setters ──────────────────────────────────────
    markUncancellableFlowRun: (sessionId: string) => void
    clearUncancellableFlowRun: (sessionId: string) => void
  }

const DEEP_INTRO_DISMISSED_KEY = "deep-intro-dismissed"

function readDeepIntroDismissed(): boolean {
  if (typeof window === "undefined") return false
  try {
    if (window.localStorage.getItem(DEEP_INTRO_DISMISSED_KEY) === "1") return true
    // Migrate a prior sessionStorage dismissal so returning users aren't
    // re-prompted after the storage backend change.
    if (window.sessionStorage.getItem(DEEP_INTRO_DISMISSED_KEY) === "1") {
      window.localStorage.setItem(DEEP_INTRO_DISMISSED_KEY, "1")
      window.sessionStorage.removeItem(DEEP_INTRO_DISMISSED_KEY)
      return true
    }
    return false
  } catch {
    return false
  }
}

function writeDeepIntroDismissed(dismissed: boolean): void {
  if (typeof window === "undefined") return
  try {
    if (dismissed) {
      window.localStorage.setItem(DEEP_INTRO_DISMISSED_KEY, "1")
    } else {
      window.localStorage.removeItem(DEEP_INTRO_DISMISSED_KEY)
    }
  } catch {
    /* storage quota / disabled — fall through */
  }
}

/**
 * Build a writer that merges a partial deep-state update into
 * `sessionStates[sid].deepExplorerState`. Upserts the row if missing. The
 * row's non-deep `SessionState` fields are filled from `defaultSessionState`
 * so consumers always see a fully-shaped row.
 */
function updateDeepSlot(
  state: Pick<ChatSlice, "sessionStates">,
  sessionId: string,
  patch:
    | Partial<DeepExplorerSessionState>
    | ((prev: DeepExplorerSessionState) => Partial<DeepExplorerSessionState>)
): Record<string, SessionState> {
  // The Record index type asserts non-nullable, but at runtime a missing
  // session id genuinely returns undefined — the optional chain is real
  // protection, not unnecessary.
  const prevSession: SessionState | undefined = state.sessionStates[sessionId]
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const prevDeep = prevSession?.deepExplorerState ?? defaultDeepExplorerSessionState
  const partial = typeof patch === "function" ? patch(prevDeep) : patch
  const nextDeep = { ...prevDeep, ...partial }
  const nextSession: SessionState = {
    ...defaultSessionState,
    ...prevSession,
    deepExplorerState: nextDeep,
  }
  return {
    ...state.sessionStates,
    [sessionId]: nextSession,
  }
}

/**
 * Factory function to create a new AI Explorer store instance.
 * Used internally by createNamespacedStore to create per-sessionType instances.
 */
function createAIExplorerStoreInstance() {
  return create<AIExplorerStore>()((set, get, api) => ({
    ...createChatSlice(set, get, api),
    ...createExplorerFeaturesSlice(set, get, api),
    ...createArtifactsSlice(set, get),
    // ── Deep Explorer namespace prefs ─────────────────────────────────────
    panelOpen: true,
    deepModeIntroDismissedWhileIdle: readDeepIntroDismissed(),
    uncancellableFlowRunSessionIds: new Map(),

    // ── Per-session writers ───────────────────────────────────────────────
    replaceDeepState(sessionId, partial) {
      set((state) => ({
        sessionStates: updateDeepSlot(state, sessionId, partial),
      }))
    },
    setDeepAutoApprove(sessionId, auto) {
      set((state) => ({
        sessionStates: updateDeepSlot(state, sessionId, { autoApprove: auto }),
      }))
    },
    setDeepPlanProposed(sessionId, proposed, todos = []) {
      set((state) => ({
        sessionStates: updateDeepSlot(state, sessionId, (prev) => ({
          planProposed: proposed,
          proposedTodos: todos,
          // A fresh plan proposal invalidates any prior rewind snapshot —
          // approving the new plan will capture a new one.
          planSnapshot: proposed ? null : prev.planSnapshot,
          // A new plan proposal supersedes any prior approval state for this
          // session: turn 2 re-prompts even if turn 1 was approved, and a
          // hydrated planApproved=true (from cross-tab state restore) must
          // not stick around when the next turn's plan arrives.
          ...(proposed ? { planApproved: false } : {}),
        })),
      }))
    },
    setDeepPlanApproved(sessionId, approved) {
      set((state) => ({
        sessionStates: updateDeepSlot(state, sessionId, { planApproved: approved }),
      }))
    },
    setAskUserProposed(sessionId, proposed, payload = null) {
      set((state) => ({
        sessionStates: updateDeepSlot(state, sessionId, {
          askUserProposed: proposed,
          askUserPayload: payload ?? null,
        }),
      }))
    },

    // ── Bridging setter ──────────────────────────────────────────────────
    setDeepPanelOpen(open) {
      const sid = get().currentSessionId
      set((state) => {
        // Always update the namespace pref…
        const next: Partial<AIExplorerStore> = { panelOpen: open }
        // …and, if we have an active session, also flip its dismissed flag
        // so the "default-open in a deep chat" effect can't fight a close.
        if (sid) {
          next.sessionStates = updateDeepSlot(state, sid, { panelDismissedForSession: !open })
        }
        return next
      })
    },

    // ── Namespace pref setters ───────────────────────────────────────────
    setDeepModeIntroDismissedWhileIdle(dismissed) {
      writeDeepIntroDismissed(dismissed)
      set({ deepModeIntroDismissedWhileIdle: dismissed })
    },
    markUncancellableFlowRun(sessionId) {
      set((state) => {
        const sessionIds = new Map(state.uncancellableFlowRunSessionIds)
        sessionIds.set(sessionId, (sessionIds.get(sessionId) ?? 0) + 1)
        return { uncancellableFlowRunSessionIds: sessionIds }
      })
    },
    clearUncancellableFlowRun(sessionId) {
      set((state) => {
        const count = state.uncancellableFlowRunSessionIds.get(sessionId)
        if (count === undefined) return state
        const sessionIds = new Map(state.uncancellableFlowRunSessionIds)
        if (count === 1) sessionIds.delete(sessionId)
        else sessionIds.set(sessionId, count - 1)
        return { uncancellableFlowRunSessionIds: sessionIds }
      })
    },
  }))
}

/**
 * Hook to get AI Explorer store isolated by sessionType.
 * Each sessionType (Explorer, custom variants, etc.) gets its own store instance.
 *
 * @param sessionType - Session type identifier (e.g., "variant-a", "variant-b")
 * @returns Zustand store hook for the specific sessionType
 */
export function useAIExplorerStore(sessionType: string) {
  return createNamespacedStore("ai-explorer", sessionType, createAIExplorerStoreInstance)
}
